/**
 * 0.38.69 - Information card element.
 *
 * A status badge, a title, an identifier line, a repeatable list of
 * label/value section pairs, an optional Edit button in the header,
 * and an optional full-width action button at the foot. Built to the
 * Henaket information-card spec: 368px fixed width, height computed
 * from the section count, all colours mapped to existing GT18 tokens.
 *
 * Everything is independently managed (same philosophy as the payment
 * card): every string is its own prop, and the badge / Edit button /
 * action button / each section can be shown or hidden. All display
 * strings are string-coerced before render (React #31 safe).
 *
 * Sections are provided as an array of { label, value } objects. The
 * caller (renderer) builds this array from the element's flat
 * section_N_label / section_N_value fields, dropping empty ones. The
 * component just renders whatever non-empty pairs it receives.
 *
 * Buttons here are presentational. The Edit and action buttons fire
 * optional onEdit / onAction callbacks so the renderer can wire them
 * to signalClick / navigation / commands. When no callback is given
 * they render but do nothing (matches a button with no destination).
 */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { RdSearchContext, RdRowContext } from '../lib/rdCardContext';
import { watchCardHeight, refreshCardHeights } from '../lib/equalCardHeights';
import styles from './InformationCard.module.css';
import { chipStyle } from '../lib/readableText';
import BuiltinIcon, { iconColorValue } from './BuiltinIcon';
import { colorCss } from '../lib/colorCss';
import { cardColorVars } from '../lib/cardColors';
import { INFO_HEADER_PARTS, INFO_PART_FIXED } from '../lib/infoCardParts';

const _s = (v) => (v == null ? '' : (typeof v === 'string' ? v : String(v)));

/**
 * Props:
 *   badge         string  (status chip; hidden when empty)
 *   badgeColor    string  (chip fill: token or #RRGGBB; empty = Info light)
 *   title         string
 *   idText        string  (identifier line; hidden when empty)
 *   sections      [{ label, value }]  (empty pairs already filtered out)
 *   showEdit      bool    (Edit button in header)
 *   editLabel     string
 *   onEdit        () => void
 *   showAction    bool    (full-width foot button)
 *   actionLabel   string
 *   onAction      () => void
 *   className     optional extra class on the outer card
 */
/* 0.48.14 - Whether the screen is a phone's (narrower than 768px, the
 * platform's phone layout), followed as the window is resized. Only
 * listened to when a card needs it. */
const PHONE_QUERY = '(max-width: 767.98px)';
// 0.49.35 - A card's Details opened (closes the others).
const DETAILS_EVENT = 'gt18-card-details-open';

/* 0.49.37 - After a card opens, scroll so that all of it shows: its
 * foot just above the bottom of the screen, as long as its top stays
 * in view (a card taller than the screen comes to the top instead).
 * No scrolling when it already shows whole.
 *
 * 0.49.40 - In step with the opening: while the card grows, every
 * frame moves the page by what the card's foot passed the bottom of
 * the screen, so the page scrolls as the card opens instead of after
 * it. `then` runs once the card has finished opening. */
function revealCard(el, then) {
  const done = () => { if (then) then(); };
  if (!el || typeof window === 'undefined' || !window.requestAnimationFrame) { done(); return; }
  const margin = 16;
  const topLimit = () => {
    // A header fixed or sticky at the top covers part of the screen.
    let top = margin;
    // 0.49.76 - And a Line group kept at the top of the screen.
    for (const h of document.querySelectorAll('header, [data-sticky-header], [data-pin-top]')) {
      const cs = window.getComputedStyle(h);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      const hr = h.getBoundingClientRect();
      if (hr.top <= 1 && hr.bottom > 0) top = Math.max(top, hr.bottom + margin);
    }
    return top;
  };
  const top = topLimit();
  const now = () => (window.performance ? performance.now() : Date.now());
  const until = now() + 340; // the opening takes 250 ms
  const step = () => {
    if (!el.isConnected) { done(); return; }
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    let delta = 0;
    // 0.49.76 - A Line group kept at the bottom covers the foot of the screen.
    let foot = 0;
    for (const b of document.querySelectorAll('[data-pin-bottom]')) {
      const br = b.getBoundingClientRect();
      if (br.height > 0 && br.bottom >= vh - 1 && br.top < vh) foot = Math.max(foot, vh - br.top);
    }
    if (r.bottom > vh - foot - margin) delta = r.bottom - (vh - foot - margin);
    // Never past the card's top (a card taller than the screen stops
    // with its top in view). Only downwards while it opens.
    if (r.top - delta < top) delta = Math.max(0, r.top - top);
    if (delta >= 1) window.scrollBy({ top: delta, behavior: 'auto' });
    if (now() < until) window.requestAnimationFrame(step);
    else done();
  };
  window.requestAnimationFrame(step);
}

/* 0.49.38 - Close the other cards, and keep this one where it is on the
 * screen while they do: a card closing above it would otherwise pull it
 * up. The page is moved back by what it moved, frame by frame, while
 * the others close (where the browser already keeps it in place, there
 * is nothing to move back). */
function closeOthersKeepingPlace(el, id) {
  if (typeof window === 'undefined') return;
  const startTop = el && el.isConnected ? el.getBoundingClientRect().top : null;
  window.dispatchEvent(new CustomEvent(DETAILS_EVENT, { detail: { id } }));
  if (startTop === null) return;
  const until = (window.performance ? performance.now() : Date.now()) + 400;
  const step = () => {
    if (!el.isConnected) return;
    const moved = el.getBoundingClientRect().top - startTop;
    if (Math.abs(moved) >= 1) window.scrollBy({ top: moved, behavior: 'auto' });
    if ((window.performance ? performance.now() : Date.now()) < until) window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
}
function usePhone(active) {
  const read = () => (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(PHONE_QUERY).matches : false);
  const [phone, setPhone] = useState(active ? read : false);
  useEffect(() => {
    if (!active || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE_QUERY);
    const on = () => setPhone(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, [active]);
  return active && phone;
}

/* 0.44.47 - The image area's padding choices, in px. */
export const IMAGE_PADDINGS = [0, 2, 4, 8, 16, 32];

export default function InformationCard({
  badge,
  /* 0.46.39 - The badge's fill: a Henaket token or '#RRGGBB'. Empty is
     the standard Info light. The text turns white on a dark fill. */
  badgeColor = '',
  // 0.46.43 - The badge's outline color (empty: none).
  badgeStroke = '',
  // 0.48.05 - The badge's text color (empty: dark, or white on a dark fill).
  badgeTextColor = '',
  title,
  idText,
  // 0.41.95 - When set, an image replaces the divider rule. Absent,
  // the card is exactly as it was.
  imageSrc = '',
  imageAlt = '',
  // 0.44.47 - Space inside the image area, around the picture: 0
  // (default, the picture fills the area), 2, 4, 8, 16 or 32 px.
  imagePadding = 0,
  /* 0.45.82 - True when the card is meant to show a picture. With no
     picture to show (none given, or it fails to load) the image area
     is kept and filled with the "no picture" box: Henaket Text 100 with
     an X in the error colour, so a missing picture reads as missing
     and cards in a grid stay the same height. */
  imageExpected = false,
  sections = [],
  showEdit = false,
  editLabel = 'Edit',
  onEdit = null,
  showAction = false,
  actionLabel = '',
  onAction = null,
  /* 0.42.28 - The whole card is the button.
   *
   * It does EXACTLY what the action button does - same handler - and
   * the visible buttons are hidden, because a button inside a button is
   * invalid HTML: a screen reader cannot say which one it is on, and
   * browsers disagree about which receives the click. */
  asButton = false,
  /* 0.42.42 - 'big' | 'medium' | 'small'. Anything else is medium, the
     width every existing card already has. */
  size = 'medium',
  /* 0.47.18 - An icon in a 40 x 40 area, under the status badge (at the
     top left when there is no badge): a platform icon (iconName, in
     iconColor) or an uploaded picture (iconSrc, which wins). */
  iconName = '',
  iconSrc = '',
  iconColor = '',
  /* 0.47.18 - The divider line: its color (a Henaket token or
     '#RRGGBB'; empty is Henaket Blue 300), or not shown at all. */
  dividerColor = '',
  dividerHidden = false,
  /* 0.49.35 - Details: the divider opens and closes what is below it.
     A link at the right just above the divider (Henaket link, with a
     chevron) shows detailsLabel while closed and detailsHideLabel while
     open. detailsStart: 'closed' (default), 'open', or 'open_large'
     (open on large screens, closed on phones). detailsKeepAction:
     the action button stays visible while closed (default). */
  details = false,
  detailsLabel = '',
  detailsHideLabel = '',
  detailsStart = 'closed',
  detailsKeepAction = true,
  /* 0.49.64 - The divider line while closed: shown (default) or not.
     Open, it is always shown (under the link). */
  detailsDividerClosed = true,
  /* 0.49.35 - Another card's Details opening on the page closes this
     one, as an accordion. 0.49.39 - Off by default: it stays open. */
  detailsCloseOthers = false,
  /* 0.47.20 - The card's fill and stroke (empty: Henaket). */
  cardColor = '',
  cardStroke = '',
  /* 0.47.21 - A text under the icon (as the main description), and a
     color for each text: a Henaket token or '#RRGGBB', empty for the
     card's own. A section's labelColor / valueColor (in `sections`)
     wins over sectionLabelColor / sectionValueColor. */
  iconText = '',
  iconTextColor = '',
  titleColor = '',
  /* 0.49.62 - The title's level and size: 'h2' to 'h6'. Empty: as
     before, an h3 at the card title size (24 on large screens, 20 on
     phones, bold). */
  titleLevel = '',
  idTextColor = '',
  sectionLabelColor = '',
  sectionValueColor = '',
  /* 0.47.22 - One color for every line of the card (titles,
     descriptions, sections). A line's own color, then its group's
     (all section labels / values), then this one. */
  textColor = '',
  /* 0.47.26 - How the picture sits in the image area: 'contain' (the
     whole picture, the default) or 'cover' (fill the area: the shorter
     side fits, the longer is cut equally at both ends). */
  imageFit = 'contain',
  /* 0.47.47 - The parts in the admin's order (lib/infoCardParts.js):
     null for the Henaket order, drawn exactly as before. */
  partOrder = null,
  /* 0.47.62 - Where the icon goes.
     iconSide: 'left' (default) or 'right', on its own line or shared.
     iconShare: 'own' (default, a line of its own), 'above' (on the line
       of the part just above it) or 'below' (the part just below), so
       a 40px icon does not take a line to itself. With no part there
       (empty, or the icon is first / last) it keeps its own line.
     iconLineText: how the shared line's content sits beside the icon:
       'left' (default) or 'right'. */
  iconSide = 'left',
  iconShare = 'own',
  iconLineText = 'left',
  /* 0.48.14 - On phones (narrower than 768px): 'keep' (default, the
     card keeps its size), 'fill' (the card takes the whole width of
     its column) or 'fill_icon' (fills it, and the icon moves to the
     right, beside the first two lines when it is above the divider,
     the last two when it is under it). */
  phoneLayout = 'keep',
  /* 0.48.15 - The height of the tallest Information card on the same
     line (lib/equalCardHeights.js). On by default. */
  equalHeight = true,
  className = '',
}) {
  // 0.45.82 - The picture failed to load: show the "no picture" box.
  // A new address (another row, another page of results) tries again.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [imageSrc]);
  const onPhone = usePhone(phoneLayout === 'fill_icon');
  const cardRef = useRef(null);
  /* 0.49.35 - Details: open or closed. 'open_large' starts open on
     large screens and closed on phones. */
  const phoneForDetails = usePhone(!!details && detailsStart === 'open_large');
  /* 0.49.55 - In a Repeating display with a search: the cards opened by
     hand, by row (they stay open, also after the search hid them for a
     while), and the search words. */
  const rdSearch = useContext(RdSearchContext);
  const rdRow = useContext(RdRowContext);
  const handSet = rdSearch && rdRow ? rdSearch.hand : null;
  const [detailsOpen, setDetailsOpenRaw] = useState(() => detailsStart === 'open'
    || !!(handSet && handSet.has(rdRow))
    || (detailsStart === 'open_large' && !(typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia(PHONE_QUERY).matches)));
  const setDetailsOpen = (v) => {
    setDetailsOpenRaw(v);
    if (handSet) { if (v) handSet.add(rdRow); else handSet.delete(rdRow); }
  };
  /* 0.49.55 - A search word found in the hidden part opens the card by
     itself (searchOpen), so the match shows. It closes again when the
     search no longer finds a word there or is cleared. A card the
     visitor closes stays closed until the search changes. */
  const foldRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const dismissedRef = useRef(false);
  const searchKey = rdSearch ? rdSearch.wordKey : '';
  useEffect(() => { dismissedRef.current = false; }, [searchKey]);
  useEffect(() => {
    const words = rdSearch && Array.isArray(rdSearch.words) ? rdSearch.words : [];
    let want = false;
    if (details && words.length && !dismissedRef.current && foldRef.current) {
      const t = (foldRef.current.textContent || '').toLowerCase();
      want = words.some(w => t.includes(w));
    }
    if (want !== searchOpen) setSearchOpen(want);
  });
  const shown = detailsOpen || searchOpen;
  useEffect(() => {
    if (detailsStart === 'open_large') setDetailsOpenRaw(!phoneForDetails || !!(handSet && handSet.has(rdRow)));
  }, [detailsStart, phoneForDetails]);
  useEffect(() => {
    if (detailsStart === 'open') setDetailsOpenRaw(true);
    else if (detailsStart === 'closed') setDetailsOpenRaw(!!(handSet && handSet.has(rdRow)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsStart]);
  const detailsIdRef = useRef(`icd-${Math.random().toString(36).slice(2, 9)}`);
  const detailsId = detailsIdRef.current;
  useEffect(() => {
    if (!details || !detailsCloseOthers || typeof window === 'undefined') return undefined;
    const on = (ev) => { if (ev.detail && ev.detail.id !== detailsId) setDetailsOpen(false); };
    window.addEventListener(DETAILS_EVENT, on);
    return () => window.removeEventListener(DETAILS_EVENT, on);
  }, [details, detailsCloseOthers, detailsId]);
  useEffect(() => (equalHeight ? watchCardHeight(cardRef.current) : undefined), [equalHeight]);
  /* 0.49.47 - Opening or closing changes what the cards beside it need:
     worked out again at once and when the opening has finished. */
  useEffect(() => {
    if (!equalHeight || !details) return undefined;
    refreshCardHeights();
    const t = setTimeout(refreshCardHeights, 320);
    return () => clearTimeout(t);
  }, [shown, equalHeight, details]);
  const phoneClass = phoneLayout === 'fill' || phoneLayout === 'fill_icon' ? styles.phoneFill : '';
  const badgeText = _s(badge);
  const titleText = _s(title);
  const idLine = _s(idText);
  // 0.38.79 - A section renders only when it has a non-empty LABEL.
  // Was: label OR value non-empty (a value with no label still
  // showed). Per admin, an empty label means the whole section -
  // including its value - is hidden. The label is the anchor; a
  // value with no label has no meaning on the card.
  const cleanSections = Array.isArray(sections)
    ? sections
        .map((s, i) => ({ label: _s(s && s.label), value: _s(s && s.value),
                     labelColor: s && s.labelColor, valueColor: s && s.valueColor,
                     n: (s && s.n) || i + 1 }))
        // 0.45.87 - A section shows when it has a label OR a value: a
        // value alone is a plain (not bold) line, a label alone a bold
        // one, so the two can be mixed freely.
        .filter(s => s.label !== '' || s.value !== '')
    : [];

  // 0.42.28 - Only when there is something to do. A card that looks
  // clickable and does nothing is worse than one that does not look it.
  const colorStyle = (...cs) => {
    const c = cs.map(colorCss).find(Boolean) || colorCss(textColor);
    return c ? { color: c } : undefined;
  };
  const iconLine = _s(iconText);
  const cardIsButton = asButton && typeof onAction === 'function';
  // 0.49.35 - Not on a card that is itself the button (one click target).
  const detailsOn = !!details && !cardIsButton;
  const buttonProps = cardIsButton ? {
    role: 'button',
    tabIndex: 0,
    // What a screen reader announces: the card's title, since there is
    // no visible button label to read.
    'aria-label': titleText || badgeText || actionLabel || 'Open',
    onClick: onAction,
    onKeyDown: (ev) => {
      // Enter and Space, as a native button. Space would otherwise
      // scroll the page, so its default is prevented.
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        onAction();
      }
    },
  } : {};

  /* 0.47.47 - Each part on its own, for the admin's order. */
  const badgeNode = badgeText !== '' ? <span className={styles.badge} style={chipStyle(badgeColor, badgeStroke, badgeTextColor)}>{badgeText}</span> : null;
  const iconNode = (iconSrc || iconName) ? (
    <span className={styles.icon} aria-hidden="true">
      {iconSrc
        ? <img src={iconSrc} alt="" className={styles.iconImg} />
        : <BuiltinIcon name={iconName} size={40} color={iconColorValue(iconColor)} />}
    </span>
  ) : null;
  const iconTextNode = iconLine !== '' ? <p className={styles.iconText} data-align-part="icon_text" style={colorStyle(iconTextColor)}>{iconLine}</p> : null;
  const TitleTag = /^h[2-6]$/.test(titleLevel) ? titleLevel : 'h3';
  const titleSize = /^h[2-6]$/.test(titleLevel)
    ? { fontSize: `var(--font-size-${titleLevel})`, lineHeight: `var(--line-height-${titleLevel}-text)` } : null;
  const titleStyle = { ...(titleSize || {}), ...(colorStyle(titleColor) || {}) };
  const titleNode = titleText !== '' ? <TitleTag className={styles.title} data-align-part="title" style={titleStyle}>{titleText}</TitleTag> : null;
  const editNode = showEdit && !cardIsButton ? (
    <button type="button" className={styles.edit} onClick={onEdit || undefined}>
      {editLabel == null ? 'Edit' : _s(editLabel)}
    </button>
  ) : null;
  const dividerNode = imageSrc && !imageFailed
    ? (
      <div className={styles.dividerImage}>
        <img className={styles.dividerImageFill} src={imageSrc}
             alt={imageAlt || ''} loading="lazy"
             onError={() => setImageFailed(true)}
             style={{
               ...(IMAGE_PADDINGS.includes(Number(imagePadding)) && Number(imagePadding) > 0
                 ? { padding: `${Number(imagePadding)}px` } : {}),
               ...(imageFit === 'cover' ? { objectFit: 'cover', objectPosition: 'center' } : {}),
             }} />
      </div>
    )
    : (dividerHidden ? null : (
      <hr className={styles.divider}
          style={colorCss(dividerColor) ? { borderTopColor: colorCss(dividerColor) } : undefined} />
    ));
  const sectionNode = (sec) => (
    <dl className={styles.sections}>
      <div className={styles.section}>
        {/* 0.49.46 - Lined up with the same line (by place: the 1st
            shown, the 2nd...) of the cards beside it. */}
        {sec.label !== '' && <dt className={styles.label} data-align-part={`sec${cleanSections.indexOf(sec) + 1}_label`} style={colorStyle(sec.labelColor, sectionLabelColor)}>{sec.label}</dt>}
        {sec.value !== '' && <dd className={styles.value} data-align-part={`sec${cleanSections.indexOf(sec) + 1}_value`} style={colorStyle(sec.valueColor, sectionValueColor)}>{sec.value}</dd>}
      </div>
    </dl>
  );
  const actionNode = showAction && !cardIsButton ? (
    <button type="button" className={styles.action} onClick={onAction || undefined}>
      {actionLabel == null ? 'Button' : _s(actionLabel)}
    </button>
  ) : null;

  /* 0.47.62 - An icon on the right, or sharing a line, is drawn in the
     part-by-part layout: the admin's order, or the Henaket one. */
  const besideTwo = !!iconNode && phoneLayout === 'fill_icon' && onPhone;
  const iconPlaced = !!iconNode && (besideTwo || iconSide === 'right' || iconShare === 'above' || iconShare === 'below');
  const order = (Array.isArray(partOrder) && partOrder.length)
    ? partOrder
    : ((iconPlaced || detailsOn) ? [...INFO_PART_FIXED, ...cleanSections.map(x => `section_${x.n}`)] : null);

  if (order) {
    /* The admin's order: one column, 8px between two header parts
       (badge, icon, icon description, title, main description) as in
       the Henaket header, 16px around anything else, as between the
       header, the divider and the sections. Edit stays at the top
       right; the action button at the foot. */
    const nodes = [];
    for (const p of order) {
      let node = null;
      if (p === 'badge') node = badgeNode;
      else if (p === 'icon') node = iconNode;
      else if (p === 'icon_text') node = iconTextNode;
      else if (p === 'title') node = titleNode;
      else if (p === 'id_text') node = idLine !== '' ? <p className={styles.id} data-align-part="id_text" style={colorStyle(idTextColor)}>{idLine}</p> : null;
      else if (p === 'divider') node = dividerNode;
      else {
        const m = /^section_(\d+)$/.exec(p);
        const sec = m && cleanSections.find(x => x.n === Number(m[1]));
        node = sec ? sectionNode(sec) : null;
      }
      if (node) nodes.push({ p, node });
    }
    /* 0.47.62 - The icon shares the line of the part above or below
       it: one row, the icon at its side (40 x 40, centred on the
       line), the part taking the rest, its content at the left or the
       right. The row keeps the part's place and spacing. */
    const right = iconSide === 'right';
    const iconAlone = (n) => (right
      ? <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{n}</div>
      : n);
    const ii = nodes.findIndex(x => x.p === 'icon');
    if (ii >= 0 && besideTwo) {
      /* 0.48.14 - On phones: the icon in a column of its own on the
         right, centred beside two lines. Above the divider (in the
         order) it takes the first two lines of the card, under it the
         last two. The badge counts as a line, the divider does not. */
      const dv = order.indexOf('divider');
      const top = dv < 0 || order.indexOf('icon') < dv;
      // Lines on the icon's own side of the divider only: a section
      // (its label and value) counts as one line.
      const lines = nodes.map((x, k) => ({ ...x, k }))
        .filter(x => x.p !== 'icon' && x.p !== 'divider')
        .filter(x => dv < 0 || (top ? order.indexOf(x.p) < dv : order.indexOf(x.p) > dv));
      const pick = top ? lines.slice(0, 2) : lines.slice(-2);
      if (pick.length) {
        const gapIn = pick.length === 2 && INFO_HEADER_PARTS.has(pick[0].p) && INFO_HEADER_PARTS.has(pick[1].p) ? 8 : 16;
        const row = (
          <div className={styles.iconRow} data-icon-side="right" data-icon-beside="2">
            <div className={styles.iconRowPart}>
              {pick.map((x, k) => (
                <div key={x.p} className={styles.flowPart} style={{ marginTop: k ? gapIn : 0 }}>{x.node}</div>
              ))}
            </div>
            {iconNode}
          </div>
        );
        const first = pick[0].k;
        const last = pick[pick.length - 1].k;
        const merged = { p: nodes[first].p, pEnd: nodes[last].p, node: row, withIcon: true };
        const rest = nodes.filter((x, k) => k !== ii && !pick.some(y => y.k === k));
        const at = nodes.slice(0, first).filter((x, k) => k !== ii && !pick.some(y => y.k === k)).length;
        rest.splice(at, 0, merged);
        nodes.length = 0;
        nodes.push(...rest);
      } else {
        nodes[ii] = { p: 'icon', node: <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{iconNode}</div> };
      }
    } else if (ii >= 0) {
      const j = iconShare === 'above' ? ii - 1 : iconShare === 'below' ? ii + 1 : -1;
      if (j >= 0 && j < nodes.length) {
        const partner = nodes[j];
        const row = (
          <div className={styles.iconRow} data-icon-side={right ? 'right' : 'left'}>
            {!right && iconNode}
            <div className={styles.iconRowPart}
                 style={iconLineText === 'right' ? { textAlign: 'right' } : undefined}>
              {partner.node}
            </div>
            {right && iconNode}
          </div>
        );
        nodes[j] = { p: partner.p, node: row, withIcon: true };
        nodes.splice(ii, 1);
      } else {
        nodes[ii] = { p: 'icon', node: iconAlone(iconNode) };
      }
    }
    const partEl = ({ p, node }, i, list) => {
      const prev = i > 0 ? (list[i - 1].pEnd || list[i - 1].p) : null;
      const gap = i === 0 ? 0 : (INFO_HEADER_PARTS.has(p) && INFO_HEADER_PARTS.has(prev) ? 8 : 16);
      return (
        <div key={p} className={styles.flowPart} data-part={p} style={{ marginTop: gap }}>
          {node}
        </div>
      );
    };
    /* 0.49.35 - Details: the parts after the divider (in the drawn
       order) fold away, with the link just above the divider. With no
       divider drawn (hidden) the link sits where it would be. */
    let body = nodes.map((x, i) => partEl(x, i, nodes));
    // 0.49.42 - A button shown only while open folds with the open part.
    let actionInFold = false;
    if (detailsOn) {
      const dvAt = order.indexOf('divider');
      let cut = nodes.findIndex(x => x.p === 'divider');
      const hasDivider = cut >= 0;
      if (!hasDivider) cut = nodes.filter(x => order.indexOf(x.pEnd || x.p) < dvAt).length;
      const before = nodes.slice(0, hasDivider ? cut + 1 : cut);
      const after = nodes.slice(hasDivider ? cut + 1 : cut);
      if (after.length) {
        const linkText = shown ? (_s(detailsHideLabel) || 'Hide') : (_s(detailsLabel) || 'Details');
        const link = (
          <div key="__details" className={styles.detailsRow}
               style={{ marginTop: before.length ? (hasDivider ? 8 : 16) : 0 }}>
            <button type="button" className={styles.detailsLink}
                    aria-expanded={shown} aria-controls={detailsId}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      const next = !shown;
                      setDetailsOpen(next);
                      // 0.49.55 - Closing a card the search opened.
                      if (!next && searchOpen) { dismissedRef.current = true; setSearchOpen(false); }
                      // 0.49.35 - Opening it closes the other cards that close (default).
                      if (next && typeof window !== 'undefined') {
                        /* 0.49.38 - In this order: this card opens, the
                           page scrolls to show it, and only then do the
                           other cards close (keeping this one still). */
                        const el = cardRef.current;
                        revealCard(el, () => closeOthersKeepingPlace(el, detailsId));
                      }
                    }}>
              {linkText}
              <svg className={styles.detailsChevron} viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        );
        const beforeEls = before.map((x, i) => partEl(x, i, before));
        // The divider comes right after the link (8px under it).
        if (hasDivider) {
          beforeEls.pop();
          body = [...beforeEls, link,
            <div key="divider" className={styles.flowPart} data-part="divider"
                 style={{ marginTop: 8, ...(!shown && !detailsDividerClosed ? { display: 'none' } : {}) }}>{nodes[cut].node}</div>];
        } else {
          body = [...beforeEls, link];
        }
        body.push(
          <div key="__fold" id={detailsId} ref={foldRef} className={styles.detailsFold} data-details-fold=""
               data-open={shown ? 'true' : 'false'}
               inert={shown ? undefined : ''}
               aria-hidden={shown ? undefined : 'true'}>
            <div className={styles.detailsFoldInner}>
              {after.map((x, i) => partEl(x, i === 0 ? 1 : i, i === 0 ? [{ p: 'divider' }, ...after] : after))}
              {/* 0.49.42 - The button that shows only while open sits
                  inside the open part: it opens and closes with it, and
                  a card's closed height (for the cards beside it) never
                  counts it. It was outside and made the closed cards on
                  the line as tall as an open card's button. */}
              {!detailsKeepAction && actionNode && (
                <div className={styles.detailsAction} data-details-action="">{actionNode}</div>
              )}
            </div>
          </div>,
        );
        actionInFold = !detailsKeepAction && !!actionNode;
      }
    }
    return (
      <div data-information-card="" data-equal-height={equalHeight ? "" : undefined} ref={cardRef} className={`${styles.card} ${styles.flow} ${size === 'big' ? styles.sizeBig : size === 'small' ? styles.sizeSmall : ''} ${phoneClass} ${cardIsButton ? styles.asButton : ''} ${className}`}
           style={cardColorVars(cardColor, cardStroke)}
           {...buttonProps}>
        <div className={styles.flowBody}>
          {editNode && <div className={styles.flowEdit}>{editNode}</div>}
          {body}
        </div>
        {actionInFold ? null : ((!detailsOn || shown || detailsKeepAction) ? actionNode : null)}
      </div>
    );
  }

  return (
    <div data-information-card="" data-equal-height={equalHeight ? "" : undefined} ref={cardRef} className={`${styles.card} ${size === 'big' ? styles.sizeBig : size === 'small' ? styles.sizeSmall : ''} ${phoneClass} ${cardIsButton ? styles.asButton : ''} ${className}`}
         style={cardColorVars(cardColor, cardStroke)}
         {...buttonProps}>
      <div className={`${styles.header} ${showEdit && !cardIsButton ? styles.headerWithEdit : ''}`}>
        <div className={styles.main}>
          {badgeText !== '' && <span className={styles.badge} style={chipStyle(badgeColor, badgeStroke, badgeTextColor)}>{badgeText}</span>}
          {(iconSrc || iconName) && (
            <span className={styles.icon} aria-hidden="true">
              {iconSrc
                ? <img src={iconSrc} alt="" className={styles.iconImg} />
                : <BuiltinIcon name={iconName} size={40} color={iconColorValue(iconColor)} />}
            </span>
          )}
          {iconLine !== '' && (
            <p className={styles.iconText} data-align-part="icon_text" style={colorStyle(iconTextColor)}>{iconLine}</p>
          )}
          {titleText !== '' && <TitleTag className={styles.title} data-align-part="title" style={titleStyle}>{titleText}</TitleTag>}
        </div>
        {idLine !== '' && (
          <p className={styles.id} data-align-part="id_text" title={idLine} style={colorStyle(idTextColor)}>{idLine}</p>
        )}
        {showEdit && !cardIsButton && (
          <button type="button" className={styles.edit}
                  onClick={onEdit || undefined}>
            {editLabel == null ? 'Edit' : _s(editLabel)}
          </button>
        )}
      </div>

      {/* 0.41.95 - The divider is an image when one is set.
          Same slot, same spacing: the card's rhythm is the Henaket
          spec's, and an image that pushed the sections down would
          make two cards side by side stop lining up. */}
      {imageSrc && !imageFailed
        /* 0.45.94 - The square is a box of its own, and the picture
           fills it absolutely. An <img> carrying the square itself
           took its own height in some browsers when the card is a
           stretched flex column (its minimum height became the
           picture's natural height), so a tall picture overflowed
           the image area. An empty box has no natural height, so the
           square holds whatever the picture is. */
        ? (
          <div className={styles.dividerImage}>
            <img className={styles.dividerImageFill} src={imageSrc}
                 alt={imageAlt || ''} loading="lazy"
                 onError={() => setImageFailed(true)}
                 style={{
                   ...(IMAGE_PADDINGS.includes(Number(imagePadding)) && Number(imagePadding) > 0
                     ? { padding: `${Number(imagePadding)}px` } : {}),
                   ...(imageFit === 'cover' ? { objectFit: 'cover', objectPosition: 'center' } : {}),
                 }} />
          </div>
        )
        /* 0.45.93 - No picture (none in the row, or it failed to
           load): the plain divider line, with no space kept for it. */
        : (dividerHidden ? null : (
          <hr className={styles.divider}
              style={colorCss(dividerColor) ? { borderTopColor: colorCss(dividerColor) } : undefined} />
        ))}

      {cleanSections.length > 0 && (
        <dl className={styles.sections}>
          {cleanSections.map((s, i) => (
            <div key={i} className={styles.section}>
              {s.label !== '' && <dt className={styles.label} data-align-part={`sec${i + 1}_label`} style={colorStyle(s.labelColor, sectionLabelColor)}>{s.label}</dt>}
              {s.value !== '' && <dd className={styles.value} data-align-part={`sec${i + 1}_value`} style={colorStyle(s.valueColor, sectionValueColor)}>{s.value}</dd>}
            </div>
          ))}
        </dl>
      )}

      {showAction && !cardIsButton && (
        <button type="button" className={styles.action}
                onClick={onAction || undefined}>
          {actionLabel == null ? 'Button' : _s(actionLabel)}
        </button>
      )}
    </div>
  );
}
